import { useEffect, useMemo, useState } from 'react';
import { FolderDown, Search, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  useAdminEmployers,
  useChecklistAction,
  useEmployerStatusAction,
} from '../../hooks/useAdminEmployers';
import type { AdminEmployerProfile, EmployerFilters, EmployerVerificationStatus } from '../../api/adminEmployers';

const tabs: Array<{ value: EmployerVerificationStatus | 'all'; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'needs_more_info', label: 'Needs info' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

export function EmployerVerification() {
  const [activeTab, setActiveTab] = useState<EmployerVerificationStatus | 'all'>('pending');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [auditEmployer, setAuditEmployer] = useState<AdminEmployerProfile | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const filters = useMemo<EmployerFilters>(
    () => ({
      status: activeTab === 'all' ? undefined : activeTab,
      query: debouncedSearch || undefined,
    }),
    [activeTab, debouncedSearch],
  );

  const { data, isLoading, isError, refetch, isRefetching } = useAdminEmployers(filters);
  const statusAction = useEmployerStatusAction(filters);
  const checklistAction = useChecklistAction(filters);

  const employers = data ?? [];

  const handleStatusUpdate = async (employer: AdminEmployerProfile, status: EmployerVerificationStatus, note?: string) => {
    try {
      await statusAction.mutateAsync({ id: employer.id, status, note });
      toast.success(`Employer marked ${status.replace('_', ' ')}.`);
    } catch (error) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : 'Unable to update employer status.');
    }
  };

  return (
    <div className="min-h-screen bg-[#020614] pb-24">
      <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8 space-y-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.4em] text-[#f7ef8a]">Admin · Employers</p>
            <h1 className="mt-2 text-3xl font-semibold text-white">Employer verification</h1>
            <p className="text-sm text-gray-400">Approve trusted partners with full KYC checks and a document viewer.</p>
          </div>
          <div className="relative w-full sm:w-80">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
            <Input
              className="border-white/10 bg-white/[0.08] pl-9 text-sm text-white placeholder:text-gray-500"
              placeholder="Search company, contact, email…"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as EmployerVerificationStatus | 'all')} className="space-y-6">
          <TabsList className="flex flex-wrap gap-2 border border-white/10 bg-white/5 p-2">
            {tabs.map((tab) => (
              <TabsTrigger
                key={tab.value}
                value={tab.value}
                className="text-sm capitalize text-gray-200 data-[state=active]:bg-gold-gradient data-[state=active]:text-black"
              >
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value={activeTab} className="space-y-4">
            {isError && (
              <Card className="border-red-500/40 bg-red-500/10">
                <CardContent className="flex items-center justify-between p-6 text-sm text-red-100">
                  <div className="flex items-center gap-2">
                    <ShieldAlert className="h-5 w-5" />
                    Unable to load employers.
                  </div>
                  <Button variant="outline" className="border-white/20 text-white" onClick={() => refetch()}>
                    Retry
                  </Button>
                </CardContent>
              </Card>
            )}

            {!isError && (isLoading || isRefetching) && (
              <Card className="border-white/10 bg-white/[0.02]">
                <CardContent className="p-8 text-sm text-gray-400">Running compliance checks…</CardContent>
              </Card>
            )}

            {!isLoading &&
              !isError &&
              employers.map((employer) => (
                <EmployerCard
                  key={employer.id}
                  employer={employer}
                  onApprove={() => handleStatusUpdate(employer, 'approved', 'Verified via admin panel.')}
                  onReject={() => handleStatusUpdate(employer, 'rejected', 'Rejected due to missing documents.')}
                  onRequestInfo={() => handleStatusUpdate(employer, 'needs_more_info', 'Requested additional documents.')}
                  onChecklistToggle={async (checklistId, completed) => {
                    try {
                      await checklistAction.mutateAsync({ id: employer.id, checklistId, completed });
                    } catch (error) {
                      console.error(error);
                      toast.error('Unable to update checklist.');
                    }
                  }}
                  onViewAudit={() => setAuditEmployer(employer)}
                />
              ))}

            {!isLoading && !isError && employers.length === 0 && (
              <Card className="border-white/10 bg-white/[0.02]">
                <CardContent className="p-12 text-center text-sm text-gray-400">
                  No employers in this bucket right now.
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={Boolean(auditEmployer)} onOpenChange={(open) => setAuditEmployer(open ? auditEmployer : null)}>
        <DialogContent className="border-white/10 bg-[#070a1f] text-white sm:max-w-xl">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold">Audit trail · {auditEmployer?.companyName}</DialogTitle>
          </DialogHeader>
          <div className="mt-4 max-h-[60vh] space-y-4 overflow-y-auto pr-2 text-sm text-gray-200">
            {auditEmployer?.auditLog.map((event) => (
              <div key={event.id} className="rounded-md border border-white/5 bg-white/[0.03] p-3">
                <p className="text-xs uppercase tracking-[0.3em] text-gray-500">{new Date(event.createdAt).toLocaleString('en-GB')}</p>
                <p className="mt-1 font-semibold text-white">{event.action.replace('_', ' ')}</p>
                <p className="text-xs text-gray-400">Actor: {event.actor}</p>
                {event.note && <p className="mt-1 text-gray-300">{event.note}</p>}
              </div>
            ))}
            {auditEmployer?.auditLog.length === 0 && <p>No audit entries.</p>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

type EmployerCardProps = {
  employer: AdminEmployerProfile;
  onApprove: () => Promise<void>;
  onReject: () => Promise<void>;
  onRequestInfo: () => Promise<void>;
  onChecklistToggle: (checklistId: string, completed: boolean) => Promise<void>;
  onViewAudit: () => void;
};

function EmployerCard({
  employer,
  onApprove,
  onReject,
  onRequestInfo,
  onChecklistToggle,
  onViewAudit,
}: EmployerCardProps) {
  const statusColor: Record<EmployerVerificationStatus, string> = {
    pending: 'bg-amber-500/20 text-amber-200',
    needs_more_info: 'bg-blue-500/20 text-blue-200',
    approved: 'bg-emerald-500/20 text-emerald-200',
    rejected: 'bg-red-500/20 text-red-200',
  };

  return (
    <Card className="border-white/10 bg-white/[0.03]">
      <CardHeader className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
        <div>
          <CardTitle className="text-xl text-white">{employer.companyName}</CardTitle>
          <p className="text-sm text-gray-400">
            {employer.contactName} · {employer.contactEmail}
          </p>
          <p className="text-xs text-gray-500">Submitted {new Date(employer.submittedAt).toLocaleString('en-GB')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge className={`${statusColor[employer.status]} capitalize`}>{employer.status.replace('_', ' ')}</Badge>
          {employer.notes && (
            <Badge className="border-white/10 bg-white/[0.07] text-gray-200">{employer.notes}</Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-6 p-6 pt-0 text-sm text-gray-200">
        <div className="rounded-lg border border-white/10 bg-white/[0.05] p-4">
          <p className="text-xs uppercase tracking-[0.3em] text-gray-500">KYC checklist</p>
          <ul className="mt-3 space-y-2">
            {employer.checklist.map((item) => (
              <li key={item.id} className="flex items-center justify-between gap-3 rounded-md border border-white/5 bg-white/[0.02] p-2">
                <label className="flex items-center gap-3 text-white">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-white/30 bg-transparent text-[#f7ef8a]"
                    checked={item.completed}
                    onChange={(event) => onChecklistToggle(item.id, event.target.checked)}
                  />
                  <span>{item.label}</span>
                </label>
                {item.completedAt && (
                  <span className="text-xs text-gray-400">{new Date(item.completedAt).toLocaleString('en-GB')}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-lg border border-white/10 bg-white/[0.05] p-4">
          <p className="text-xs uppercase tracking-[0.3em] text-gray-500">Documents</p>
          <div className="mt-3 grid gap-3 md:grid-cols-2">
            {employer.documents.map((doc) => (
              <div key={doc.id} className="rounded-md border border-white/10 bg-white/[0.02] p-3">
                <p className="font-semibold text-white">{doc.name}</p>
                <p className="text-xs text-gray-400">
                  {doc.type.replace('_', ' ')} · {new Date(doc.uploadedAt).toLocaleString('en-GB')}
                </p>
                <a
                  href={doc.url}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-2 inline-flex items-center text-xs text-[#f7ef8a] hover:text-white"
                >
                  <FolderDown className="mr-2 h-4 w-4" />
                  View document
                </a>
              </div>
            ))}
            {employer.documents.length === 0 && <p className="text-sm text-gray-400">No documents uploaded.</p>}
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          {employer.status !== 'approved' && (
            <Button className="bg-gold-gradient text-black hover:brightness-110" onClick={onApprove}>
              Approve
            </Button>
          )}
          {employer.status !== 'rejected' && (
            <Button variant="outline" className="border-red-400/30 text-red-200" onClick={onReject}>
              Reject
            </Button>
          )}
          {employer.status !== 'needs_more_info' && employer.status !== 'approved' && (
            <Button variant="outline" className="border-white/20 text-white" onClick={onRequestInfo}>
              Request info
            </Button>
          )}
          <Button variant="ghost" className="text-[#f7ef8a] hover:text-white" onClick={onViewAudit}>
            View audit log
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
